import { STORAGE_KEYS } from '@/app/config/constants';

/**
 * localStorage persistence for the session, per mdfiles/loginapi.md section 5.
 *
 * Everything lives in localStorage because the document specifies it -- keys
 * `accessToken`, `refreshToken`, `tokenType`, `expiresIn`, `tokenExpiry`,
 * `isAuth` plus the five profile fields. Every access is guarded: Safari
 * private mode throws on write and storage can be disabled outright, in which
 * case the app still works for the current page load off Redux state alone.
 */

interface StoredTokens {
  accessToken: string | null;
  refreshToken: string | null;
  tokenType: string;
  expiresIn: number | null;
  /** Absolute epoch ms at which the access token lapses. */
  tokenExpiry: number | null;
}

interface StoredUser {
  userId: string;
  userName: string;
  firstName: string;
  lastName: string;
  emailAddress: string;
}

const DEFAULT_TOKEN_TYPE = 'Bearer';

function store(): Storage | null {
  try {
    const probe = '__niraxobid_probe__';
    window.localStorage.setItem(probe, probe);
    window.localStorage.removeItem(probe);
    return window.localStorage;
  } catch {
    return null;
  }
}

function read(key: string): string | null {
  try {
    return store()?.getItem(key) ?? null;
  } catch {
    return null;
  }
}

function write(key: string, value: string): void {
  try {
    store()?.setItem(key, value);
  } catch {
    /* storage unavailable -- session lives in Redux for this page load only */
  }
}

function remove(key: string): void {
  try {
    store()?.removeItem(key);
  } catch {
    /* nothing to do */
  }
}

function readNumber(key: string): number | null {
  const raw = read(key);
  if (raw === null) return null;
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : null;
}

export const tokenService = {
  /* -- tokens ------------------------------------------------------------- */

  getAccessToken(): string | null {
    return read(STORAGE_KEYS.ACCESS_TOKEN);
  },

  getRefreshToken(): string | null {
    return read(STORAGE_KEYS.REFRESH_TOKEN);
  },

  getTokenType(): string {
    return read(STORAGE_KEYS.TOKEN_TYPE) ?? DEFAULT_TOKEN_TYPE;
  },

  getTokens(): StoredTokens {
    return {
      accessToken: read(STORAGE_KEYS.ACCESS_TOKEN),
      refreshToken: read(STORAGE_KEYS.REFRESH_TOKEN),
      tokenType: read(STORAGE_KEYS.TOKEN_TYPE) ?? DEFAULT_TOKEN_TYPE,
      expiresIn: readNumber(STORAGE_KEYS.EXPIRES_IN),
      tokenExpiry: readNumber(STORAGE_KEYS.TOKEN_EXPIRY),
    };
  },

  /**
   * Persists a login or refresh payload. `tokenExpiry` is derived here
   * (`now + expiresIn * 1000`) because the API only reports a duration, and
   * `isAuth` mirrors whether an access token is present -- step 2 of the
   * document's workflow.
   */
  setTokens(tokens: {
    accessToken: string;
    refreshToken?: string;
    tokenType?: string;
    expiresIn?: number;
  }): void {
    write(STORAGE_KEYS.ACCESS_TOKEN, tokens.accessToken);
    if (tokens.refreshToken) write(STORAGE_KEYS.REFRESH_TOKEN, tokens.refreshToken);
    write(STORAGE_KEYS.TOKEN_TYPE, tokens.tokenType ?? DEFAULT_TOKEN_TYPE);

    if (typeof tokens.expiresIn === 'number' && Number.isFinite(tokens.expiresIn)) {
      write(STORAGE_KEYS.EXPIRES_IN, String(tokens.expiresIn));
      write(STORAGE_KEYS.TOKEN_EXPIRY, String(Date.now() + tokens.expiresIn * 1000));
    }

    write(STORAGE_KEYS.IS_AUTH, tokens.accessToken ? 'true' : 'false');
  },

  /** True once the stored expiry has passed. Unknown expiry counts as valid. */
  isAccessTokenExpired(skewMs = 0): boolean {
    const expiry = readNumber(STORAGE_KEYS.TOKEN_EXPIRY);
    if (expiry === null) return false;
    return Date.now() + skewMs >= expiry;
  },

  isAuthenticated(): boolean {
    return read(STORAGE_KEYS.IS_AUTH) === 'true' && Boolean(read(STORAGE_KEYS.ACCESS_TOKEN));
  },

  /* -- profile ------------------------------------------------------------ */

  setUser(user: StoredUser): void {
    write(STORAGE_KEYS.USER_ID, user.userId);
    write(STORAGE_KEYS.USER_NAME, user.userName);
    write(STORAGE_KEYS.FIRST_NAME, user.firstName);
    write(STORAGE_KEYS.LAST_NAME, user.lastName);
    write(STORAGE_KEYS.EMAIL_ADDRESS, user.emailAddress);
    write(STORAGE_KEYS.IS_AUTH, 'true');
  },

  /** Null unless a userName was stored -- the one field always present. */
  getUser(): StoredUser | null {
    const userName = read(STORAGE_KEYS.USER_NAME);
    if (!userName) return null;
    return {
      userId: read(STORAGE_KEYS.USER_ID) ?? '',
      userName,
      firstName: read(STORAGE_KEYS.FIRST_NAME) ?? '',
      lastName: read(STORAGE_KEYS.LAST_NAME) ?? '',
      emailAddress: read(STORAGE_KEYS.EMAIL_ADDRESS) ?? '',
    };
  },

  /* -- teardown ----------------------------------------------------------- */

  /** Removes every key in the schema. Used by logout and by a failed refresh. */
  clearAll(): void {
    Object.values(STORAGE_KEYS).forEach(remove);
  },
};

export type { StoredTokens, StoredUser };
